/* Interactive Site Demo — visitors customize a fake photographer website live.
   Dark Lumetier marketing surface · light inner site preview (because that's
   exactly what site visitors will see). */

import { useState } from "react";
import { Palette, Eye, ImagePlus, DollarSign, Type } from "lucide-react";

const HEROS = [
  { id: "couple", url: "https://images.unsplash.com/photo-1519741497674-611481863552?w=1400&q=70", label: "Romantic" },
  { id: "family", url: "https://images.unsplash.com/photo-1606800052052-a08af7148866?w=1400&q=70", label: "Family" },
  { id: "studio", url: "https://images.unsplash.com/photo-1494774157365-9e04c6720e47?w=1400&q=70", label: "Studio" },
  { id: "nature", url: "https://images.unsplash.com/photo-1583939003579-730e3918a45a?w=1400&q=70", label: "Nature" },
];

const THEMES = [
  { id: "gold", color: "#C9A961", name: "Gold" },
  { id: "rose", color: "#B85780", name: "Rose" },
  { id: "sage", color: "#7A8B6F", name: "Sage" },
  { id: "terra", color: "#C26B4E", name: "Terracotta" },
  { id: "ink", color: "#1c1917", name: "Ink" },
];

export default function InteractiveSiteDemo() {
  const [studio, setStudio] = useState("Allure Image");
  const [tagline, setTagline] = useState("Heirloom photography for the moments you'll remember.");
  const [emphasis, setEmphasis] = useState("the moments");
  const [theme, setTheme] = useState(THEMES[0]);
  const [hero, setHero] = useState(HEROS[0]);
  const [services, setServices] = useState([
    { name: "Family Session", price: 750 },
    { name: "Newborn Studio", price: 950 },
    { name: "Branding Half-Day", price: 1450 },
  ]);

  function updateService(i, key, val) {
    setServices((arr) => arr.map((s, idx) => (idx === i ? { ...s, [key]: val } : s)));
  }

  function renderTagline() {
    if (!emphasis || !tagline.toLowerCase().includes(emphasis.toLowerCase())) {
      return tagline;
    }
    const idx = tagline.toLowerCase().indexOf(emphasis.toLowerCase());
    return (
      <>
        {tagline.slice(0, idx)}
        <em style={{ color: theme.color }}>{tagline.slice(idx, idx + emphasis.length)}</em>
        {tagline.slice(idx + emphasis.length)}
      </>
    );
  }

  return (
    <section className="border-t border-white/[0.07] py-28" id="customize-site">
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <div className="text-[11px] uppercase tracking-[0.32em] text-stone-500 mb-5">✦ Make your website yours ✦</div>
          <h2
            className="text-4xl md:text-5xl font-light leading-tight text-stone-100"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}
          >
            Design your front page, right here.
          </h2>
          <p className="text-stone-400 mt-6 max-w-xl mx-auto text-sm leading-relaxed">
            Type your studio name. Choose your hero photo. Pick a color. Edit your prices. This is exactly what visitors will see when they Google your name.
          </p>
        </div>

        <div className="grid lg:grid-cols-[320px_1fr] gap-5">
          {/* CONTROLS (dark) */}
          <div className="space-y-3">
            {/* Studio + tagline */}
            <div className="bg-white/[0.03] border border-white/[0.08] p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-3 flex items-center gap-1.5">
                <Type className="w-3 h-3" /> Studio identity
              </div>
              <label className="block mb-3">
                <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500">Name</span>
                <input
                  value={studio}
                  onChange={(e) => setStudio(e.target.value)}
                  className="w-full bg-transparent border-b border-white/[0.15] py-1.5 outline-none focus:border-[#C9A961] text-sm text-stone-100 transition-colors"
                  data-testid="site-studio-name"
                />
              </label>
              <label className="block mb-3">
                <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500">Tagline</span>
                <textarea
                  rows={2}
                  value={tagline}
                  onChange={(e) => setTagline(e.target.value)}
                  className="w-full bg-transparent border-b border-white/[0.15] py-1.5 outline-none focus:border-[#C9A961] text-sm resize-none text-stone-100 transition-colors"
                  data-testid="site-tagline"
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-stone-500">Emphasized phrase</span>
                <input
                  value={emphasis}
                  onChange={(e) => setEmphasis(e.target.value)}
                  placeholder="(part of tagline)"
                  className="w-full bg-transparent border-b border-white/[0.15] py-1.5 outline-none focus:border-[#C9A961] text-sm text-stone-100 transition-colors placeholder:text-stone-600"
                  data-testid="site-emphasis"
                />
                <span className="text-[10px] text-stone-500 mt-1 block">Gets italicized in brand color</span>
              </label>
            </div>

            {/* Brand color */}
            <div className="bg-white/[0.03] border border-white/[0.08] p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-3 flex items-center gap-1.5">
                <Palette className="w-3 h-3" /> Brand color
              </div>
              <div className="flex flex-wrap gap-2">
                {THEMES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setTheme(t)}
                    className="w-9 h-9 transition-transform hover:scale-110"
                    style={{
                      background: t.color,
                      outline: theme.id === t.id ? `2px solid ${GOLD_OUTLINE}` : "none",
                      outlineOffset: 2,
                    }}
                    title={t.name}
                    data-testid={`site-theme-${t.id}`}
                  />
                ))}
              </div>
              <div className="text-[10px] text-stone-500 mt-3">{theme.name}</div>
            </div>

            {/* Hero photo */}
            <div className="bg-white/[0.03] border border-white/[0.08] p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-3 flex items-center gap-1.5">
                <ImagePlus className="w-3 h-3" /> Hero photo
              </div>
              <div className="grid grid-cols-2 gap-2">
                {HEROS.map((h) => (
                  <button
                    key={h.id}
                    onClick={() => setHero(h)}
                    className="aspect-[4/3] overflow-hidden relative transition-all"
                    style={{ outline: hero.id === h.id ? `2px solid ${theme.color}` : "none", outlineOffset: 2 }}
                    data-testid={`site-hero-${h.id}`}
                  >
                    <img src={h.url} alt={h.label} className="w-full h-full object-cover" />
                    <div className="absolute bottom-0 left-0 right-0 text-[9px] uppercase tracking-[0.15em] text-white py-0.5 bg-black/50">{h.label}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Services (editable prices) */}
            <div className="bg-white/[0.03] border border-white/[0.08] p-5">
              <div className="text-[10px] uppercase tracking-[0.22em] text-stone-500 mb-3 flex items-center gap-1.5">
                <DollarSign className="w-3 h-3" /> Service prices
              </div>
              <div className="space-y-2">
                {services.map((s, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <input
                      value={s.name}
                      onChange={(e) => updateService(i, "name", e.target.value)}
                      className="flex-1 bg-transparent border-b border-white/[0.1] py-1 outline-none focus:border-[#C9A961] text-xs text-stone-200 transition-colors"
                      data-testid={`site-service-name-${i}`}
                    />
                    <span className="text-xs text-stone-500">$</span>
                    <input
                      type="number"
                      value={s.price}
                      onChange={(e) => updateService(i, "price", Number(e.target.value) || 0)}
                      className="w-16 bg-transparent border-b border-white/[0.1] py-1 outline-none focus:border-[#C9A961] text-xs text-stone-200 transition-colors"
                      data-testid={`site-service-price-${i}`}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="text-[11px] text-stone-500 text-center pt-2 flex items-center justify-center gap-1.5">
              <Eye className="w-3 h-3" /> Everything updates live →
            </div>
          </div>

          {/* LIVE PREVIEW (stays light — this is what site visitors see) */}
          <div className="bg-stone-900 p-2 md:p-3" style={{ borderRadius: 4 }}>
            <div className="flex items-center gap-1.5 px-3 py-2">
              <div className="w-2.5 h-2.5 rounded-full bg-stone-700" />
              <div className="w-2.5 h-2.5 rounded-full bg-stone-700" />
              <div className="w-2.5 h-2.5 rounded-full bg-stone-700" />
              <div className="ml-3 text-[10px] text-stone-500 truncate">
                {studio.toLowerCase().replace(/[^a-z0-9]+/g, "")}.lumetiercreatives.com
              </div>
            </div>
            <div className="bg-stone-50 overflow-hidden" data-testid="site-preview">
              {/* Nav */}
              <div className="px-6 py-3 flex items-center justify-between border-b border-stone-200">
                <div className="text-xs tracking-[0.32em] font-light truncate" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                  {(studio || "YOUR STUDIO").toUpperCase()}
                </div>
                <div className="hidden sm:flex items-center gap-4 text-[9px] uppercase tracking-[0.22em] text-stone-500">
                  <span>Services</span>
                  <span>Portfolio</span>
                  <span>About</span>
                  <span className="px-2.5 h-6 inline-flex items-center text-white" style={{ background: theme.color }}>Book</span>
                </div>
              </div>
              {/* Hero */}
              <div className="relative h-64 md:h-80 overflow-hidden">
                <img src={hero.url} alt="" className="w-full h-full object-cover transition-opacity duration-500" />
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center text-center px-6"
                  style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.15), rgba(0,0,0,0.55))" }}
                >
                  <div className="text-[10px] uppercase tracking-[0.32em] text-stone-200 mb-3">
                    Booking now · {new Date().getFullYear() + 1}
                  </div>
                  <h1 className="text-2xl md:text-4xl font-light text-white max-w-2xl leading-tight" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                    {renderTagline()}
                  </h1>
                  <div className="h-px w-12 mt-4" style={{ background: theme.color }} />
                  <button className="mt-6 px-4 h-9 text-stone-900 text-[10px] uppercase tracking-[0.22em]" style={{ background: "#fff" }}>
                    Book a session →
                  </button>
                </div>
              </div>
              {/* Services strip */}
              <div className="px-6 py-8">
                <div className="text-center text-[10px] uppercase tracking-[0.32em] text-stone-500 mb-5">Signature offerings</div>
                <div className="grid sm:grid-cols-3 gap-3">
                  {services.map((s, i) => (
                    <div key={i} className="bg-white border border-stone-200 overflow-hidden">
                      <div className="aspect-[4/3] overflow-hidden">
                        <img src={HEROS[(i + 1) % HEROS.length].url} alt="" className="w-full h-full object-cover" />
                      </div>
                      <div className="p-3">
                        <div className="text-sm" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{s.name}</div>
                        <div className="text-[10px] uppercase tracking-[0.22em] mt-1" style={{ color: theme.color }}>From ${s.price}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Footer stamp */}
              <div className="px-6 py-3 border-t border-stone-100 text-[10px] text-stone-500 text-center">
                {studio || "Your Studio"} · Powered by Lumetier
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Outline color for theme swatches (gold to stay on-brand on dark)
const GOLD_OUTLINE = "#EDD78C";
